import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'src/contexts/AuthContext';
import type { ProtectionProps, UserRole } from 'src/types/auth';

/**
 * Unified Protection Component
 *
 * This component provides both route-level and content-level protection
 * based on user roles. It replaces all existing ProtectedRoute components
 * and provides a consistent, type-safe way to handle authorization.
 *
 * @example Route Protection:
 * <Protection mode="route" allowedRoles={['ADMIN', 'DISPATCHER']}>
 *   <SidebarLayout />
 * </Protection>
 *
 * @example Content Protection:
 * <Protection mode="content" allowedRoles={['ADMIN']}>
 *   <AdminPanel />
 * </Protection>
 */
const Protection: React.FC<ProtectionProps> = ({
  children,
  mode = 'route',
  allowedRoles = [],
  blockedRoles = [],
  requireAll = false,
  fallbackRoute = '/dashboards/main',
  logoutOnUnauthorized = true,
  fallbackComponent: FallbackComponent,
  loadingComponent: LoadingComponent,
  onUnauthorized,
  debugMode = false
}) => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Debug logging
  const debugLog = (message: string, data?: unknown) => {
    if (debugMode) {
      console.log(`[Protection Debug] ${message}`, data);
    }
  };

  debugLog('Protection component rendered', {
    mode,
    allowedRoles,
    blockedRoles,
    requireAll,
    path: location.pathname,
    userRoles: user?.roles
  });

  // Show loading component if available and auth is loading
  if (loading) {
    debugLog('Auth loading, showing loading component');
    if (LoadingComponent) {
      return <LoadingComponent />;
    }
    // For route mode, we might want to show a loading state
    // For content mode, we return null to avoid flash
    return mode === 'route' ? <div>Loading...</div> : null;
  }

  // Check authentication first
  if (!isAuthenticated()) {
    debugLog('User not authenticated');
    if (mode === 'route') {
      return <Navigate to="/login" replace />;
    }
    return null; // Don't render content if not authenticated
  }

  const userRoles: UserRole[] = (user?.roles as UserRole[]) || [];
  debugLog('User roles extracted', { userRoles });

  // Check blocked roles first (highest precedence)
  if (blockedRoles.length > 0) {
    const hasBlockedRole = blockedRoles.some((role) =>
      userRoles.includes(role)
    );
    debugLog('Checking blocked roles', {
      blockedRoles,
      hasBlockedRole,
      matchingRoles: blockedRoles.filter((role) => userRoles.includes(role))
    });

    if (hasBlockedRole) {
      debugLog('User has blocked role, denying access');
      return handleUnauthorizedAccess('blocked_role');
    }
  }

  // Check allowed roles
  if (allowedRoles.length > 0) {
    const hasPermission = requireAll
      ? allowedRoles.every((role) => userRoles.includes(role))
      : allowedRoles.some((role) => userRoles.includes(role));

    debugLog('Checking allowed roles', {
      allowedRoles,
      requireAll,
      hasPermission,
      matchingRoles: allowedRoles.filter((role) => userRoles.includes(role))
    });

    if (!hasPermission) {
      debugLog('User lacks required roles, denying access');
      return handleUnauthorizedAccess('insufficient_roles');
    }
  }

  // User has permission, render children
  debugLog('User authorized, rendering children');
  return <>{children}</>;

  /**
   * Handles unauthorized access based on the protection mode
   */
  function handleUnauthorizedAccess(
    reason: 'blocked_role' | 'insufficient_roles'
  ) {
    // Call custom unauthorized callback if provided
    if (onUnauthorized) {
      onUnauthorized(user, location);
    }

    // Log unauthorized access attempt
    const logData = {
      reason,
      path: location.pathname,
      userRoles,
      allowedRoles,
      blockedRoles,
      username: user?.username,
      timestamp: new Date().toISOString()
    };

    console.warn('Unauthorized access attempt:', logData);

    if (mode === 'route') {
      // Route protection: handle redirect/logout
      if (logoutOnUnauthorized) {
        debugLog('Logging out user due to unauthorized route access');
        logout();
        return (
          <Navigate
            to="/login"
            replace
            state={{
              message:
                'Access denied. Please log in with appropriate permissions.',
              from: location.pathname
            }}
          />
        );
      } else {
        debugLog('Redirecting to fallback route without logout');
        return <Navigate to={fallbackRoute} replace />;
      }
    } else {
      // Content protection: either show custom component or don't render
      if (FallbackComponent) {
        debugLog('Rendering fallback component for unauthorized content');
        return <FallbackComponent />;
      }
      debugLog('Not rendering content due to unauthorized access');
      return null; // Don't render the content
    }
  }
};

export default Protection;
